import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";

export interface PendingNumberProps {
  className?: string;
}

/**
 * Inline spinner standing in for a number while its query is still loading — replaces the old static "·"
 * dot placeholder that sat in stat tiles / worklist counts across the portal (principal feedback 23/7,
 * screenshot of the Home worklist cards: a still dot reads as frozen, not "working").
 *
 * Sized in em (`size-[0.9em]`) so it scales with whatever tabular-number type it sits inside; muted, not
 * branded — this is a wait state, not a call to action (DESIGN.md: the one-accent rule reserves Indigo for
 * things the operator can act on). `role="status"` + `aria-label` carry the accessible name directly on
 * the icon — there is no adjacent visible text for it to pair with, so it must announce itself rather than
 * going `aria-hidden`.
 */
export function PendingNumber({ className }: PendingNumberProps) {
  const { t } = useTranslation();
  return (
    <Loader2
      role="status"
      aria-label={t("state.loadingValue")}
      className={cn(
        "size-[0.9em] animate-spin text-(--text-muted) motion-reduce:animate-none",
        className,
      )}
    />
  );
}
